import type { PixelEventName } from '@xshop/shared';
import { injectScript, type BrowserPixel, type PixelPayload } from './loader';

const SCRIPT_SRC = 'https://s.pinimg.com/ct/core.js';
const SCRIPT_ID = 'xshop-pinterest-tag';

// Pinterest's own event names; it has no distinct view-content step, so a page visit stands in.
const PIN_EVENTS: Record<PixelEventName, string> = {
  PageView: 'pagevisit',
  ViewContent: 'pagevisit',
  AddToCart: 'addtocart',
  InitiateCheckout: 'custom',
  Purchase: 'checkout'
};

// The queueing stub Pinterest's snippet installs so calls before the SDK loads are kept.
interface Pintrk {
  (...args: unknown[]): void;
  queue: unknown[][];
  version?: string;
}

type PintrkWindow = Window & { pintrk?: Pintrk };

// Installs the pintrk stub unless another tag on the page already provided one.
function ensurePintrk(): Pintrk {
  const target = window as PintrkWindow;
  if (target.pintrk) return target.pintrk;

  const pintrk = ((...args: unknown[]) => { pintrk.queue.push(args); }) as Pintrk;
  pintrk.queue = [];
  pintrk.version = '3.0';
  target.pintrk = pintrk;
  injectScript(SCRIPT_SRC, SCRIPT_ID);
  return pintrk;
}

// Builds the properties Pinterest reads off a value-bearing event.
function pinProperties(payload: PixelPayload): Record<string, unknown> {
  const properties: Record<string, unknown> = { event_id: payload.eventId };
  if (payload.currency) properties.currency = payload.currency;
  if (payload.value !== undefined) properties.value = payload.value;
  if (payload.numItems !== undefined) properties.order_quantity = payload.numItems;
  if (payload.contentId) properties.product_id = payload.contentId;
  return properties;
}

// The Pinterest tag. Browser-only today — we send nothing to its Conversions API.
export const pinterestPixel: BrowserPixel = {
  // Loads one Pinterest tag per configured id.
  install(ids) {
    const pintrk = ensurePintrk();
    for (const id of ids) pintrk('load', id);
  },

  // Fires one event, carrying our event id as Pinterest's deduplication key.
  track(event, payload) {
    (window as PintrkWindow).pintrk?.('track', PIN_EVENTS[event], pinProperties(payload));
  }
};
